export const DIGITAL_INVEST_WEEK_START = "2026-10-12";
export const DIGITAL_INVEST_WEEK_END = "2026-10-18";

/**
 * Optional published ranking overrides. Live day-7 pulls can drift after
 * the week is published; pin a name here to keep the printed figure.
 */
export const DIGITAL_INVEST_PUBLISHED_VIEWS: Record<string, number> = {};

export type DigitalInvestProgramInput = {
  _id?: string;
  programId?: string;
  name: string;
  imageS3Key?: string | null;
  podcast?: { title?: string };
  avgViewsDay7?: number | null;
  avgViews?: number | null;
  avgInteractionsDay7?: number | null;
  avgInteractions?: number | null;
  uniquePaidBrandsCount?: number | null;
};

export type DigitalInvestLivestreamInput = {
  programId?: string | null;
  programName: string;
  metrics?: {
    avgViewers?: number | null;
    maxPeakViewers?: number | null;
  };
};

export type DigitalInvestPoint = {
  id: string;
  name: string;
  channelName: string;
  imageS3Key: string | null;
  views: number;
  interactions: number;
  interactionRate: number;
  liveViewers: number;
  livePeak: number;
  brandCount: number;
};

type LiveJoin = { avg: number; peak: number };

export function normalizeProgramKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

function liveFromRow(row: DigitalInvestLivestreamInput): LiveJoin {
  return {
    avg: row.metrics?.avgViewers ?? 0,
    peak: row.metrics?.maxPeakViewers ?? 0,
  };
}

export function digitalInvestMapPoints(
  programs: DigitalInvestProgramInput[],
  livestreamRows: DigitalInvestLivestreamInput[],
): DigitalInvestPoint[] {
  const liveById = new Map<string, LiveJoin>();
  const liveByName = new Map<string, LiveJoin>();
  for (const row of livestreamRows) {
    const live = liveFromRow(row);
    if (row.programId) liveById.set(String(row.programId), live);
    const key = normalizeProgramKey(row.programName);
    if (key && !liveByName.has(key)) liveByName.set(key, live);
  }

  return programs
    .map((program) => {
      const id = String(program.programId ?? program._id ?? program.name);
      const nameKey = normalizeProgramKey(program.name);
      const views = Number(
        DIGITAL_INVEST_PUBLISHED_VIEWS[nameKey] ?? program.avgViewsDay7 ?? 0,
      );
      const interactions = Number(program.avgInteractionsDay7 ?? 0);
      const live = liveById.get(id) ?? liveByName.get(nameKey);
      return {
        id,
        name: program.name,
        channelName: program.podcast?.title ?? "",
        imageS3Key: program.imageS3Key ?? null,
        views,
        interactions,
        interactionRate: views > 0 ? (interactions / views) * 100 : 0,
        liveViewers: live?.avg ?? 0,
        livePeak: live?.peak ?? 0,
        brandCount: Number(program.uniquePaidBrandsCount ?? 0),
      };
    })
    .filter((point) => point.views > 0);
}

export function digitalInvestTop10(
  points: DigitalInvestPoint[],
): DigitalInvestPoint[] {
  return [...points]
    .sort(
      (a, b) =>
        b.views - a.views ||
        b.liveViewers - a.liveViewers ||
        b.interactions - a.interactions ||
        a.name.localeCompare(b.name),
    )
    .slice(0, 10);
}
